import { useEffect, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";
import { db } from "../../../backend/firebaseConfig";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import ProgressHistory from "../../Marketing/MarketingDashabord/components/ProgressHistory";
import { RECRUITMENT_REFERENCE_STATUSES } from "./constants/recruitment";

const DISPLAY_VALUE_LABELS = {
  yes: "Yes",
  no: "No",
  regulated: "Regulated Role",
  "non-regulated": "Non-Regulated Role",
  educational: "Educational Reference",
  weekly: "Weekly",
  "twice-weekly": "Twice a week",
  "set-date": "Set a date",
  employed: "Employed",
  "self-employed": "Self-employed",
  unemployed: "Unemployed",
  "in-education": "In Education",
};

RECRUITMENT_REFERENCE_STATUSES.forEach((status) => {
  DISPLAY_VALUE_LABELS[status.id] = status.label;
});

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const getAttachmentNames = (value) =>
  (Array.isArray(value) ? value : [])
    .map((attachment) => attachment?.name || attachment?.fileName)
    .filter(Boolean)
    .join(", ");

const formatValue = (value, fieldPath = "") => {
  if (value === "" || value === null || value === undefined) return "-";
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (/attachments/i.test(fieldPath)) return getAttachmentNames(value) || "-";
  if (typeof value === "object") return JSON.stringify(value);
  const stringValue = String(value).trim();
  const normalisedValue = stringValue.toLowerCase();
  if (DISPLAY_VALUE_LABELS[normalisedValue]) {
    return DISPLAY_VALUE_LABELS[normalisedValue];
  }
  if (/^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(stringValue)) {
    return stringValue
      .replace(/-/g, " ")
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }
  return stringValue;
};

const isEmptyValue = (value) =>
  value === "" ||
  value === null ||
  value === undefined ||
  (Array.isArray(value) && value.length === 0);

const HIDDEN_EDIT_FIELDS = new Set([
  "referenceUpdatedAt",
  "referenceUpdatedBy",
  "archivedReferenceAttachments",
  "saveConfirmed",
]);

const isVisibleDataChange = (change) => {
  const fieldPath = String(change.fieldPath || "");
  if (
    HIDDEN_EDIT_FIELDS.has(fieldPath) ||
    fieldPath.startsWith("emailPreview.") ||
    fieldPath.startsWith("confirmations.")
  ) {
    return false;
  }
  if (isEmptyValue(change.previousValue) && isEmptyValue(change.newValue)) {
    return false;
  }
  return JSON.stringify(change.previousValue) !== JSON.stringify(change.newValue);
};

const formatField = (value) =>
  String(value || "-")
    .replace(/^emailPreview\./, "Email preview ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\./g, " / ")
    .replace(/^./, (character) => character.toUpperCase());

const ReferenceEditLogs = ({ applicationId, referenceId, open, onToggle, sectionRef }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("follow-up-history");

  useEffect(() => {
    if (!applicationId || !referenceId || !open) return undefined;
    setLoading(true);
    const logsQuery = query(
      collection(
        db,
        "Recruitment",
        String(applicationId).replace(/[/.#?]/g, "-"),
        "References",
        String(referenceId).replace(/[/.#?]/g, "-"),
        "Edit Logs",
      ),
      orderBy("editedAt", "desc"),
      limit(100),
    );

    return onSnapshot(
      logsQuery,
      (snapshot) => {
        setLogs(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load reference edit logs", error);
        setLogs([]);
        setLoading(false);
      },
    );
  }, [applicationId, referenceId, open]);

  const followUpLogs = logs.filter((log) => {
    const action = String(log.action || "");
    if (action === "reference_email_state_repaired" || log.details?.stateRepair) {
      return false;
    }
    return [
      "reference_email_delivered",
      "reference_email_redelivered",
      "reference_email_resent",
      "reference_email_sent",
    ].includes(action) || action.startsWith("reference_chase_");
  });
  const editLogs = logs
    .filter((log) => String(log.action || "") === "reference_saved")
    .map((log) => ({
      ...log,
      changes: (log.changes || []).filter(isVisibleDataChange),
    }))
    .filter((log) => log.changes.length > 0);
  const tabs = [
    { id: "follow-up-history", label: "Follow-up History", count: followUpLogs.length },
    { id: "edit-logs", label: "Edit Logs", count: editLogs.length },
  ];

  const getFollowUpLabel = (log) => {
    const action = String(log.action || "");
    const reason = String(log.details?.reason || "");
    if ([
      "reference_email_delivered",
      "reference_email_redelivered",
      "reference_email_resent",
      "reference_email_sent",
    ].includes(action)) return "Reference email sent";
    if (action === "reference_chase_sent") return "Follow-up sent";
    if (reason === "skip-next-follow-up") return "Follow-up skipped";
    if (action === "reference_chase_send_requested") return "Follow-up send requested";
    if (action === "reference_chase_skipped") return "Follow-up not sent";
    return action.replace(/_/g, " ");
  };

  const getFollowUpColor = (log) => {
    const action = String(log.action || "");
    let statusId = "ready-to-request";
    if (["reference_email_delivered", "reference_email_redelivered", "reference_email_resent", "reference_email_sent"].includes(action)) {
      statusId = "reference-sent";
    } else if (action === "reference_chase_sent") {
      statusId = "follow-up-1-sent";
    } else if (action === "reference_chase_skipped") {
      statusId = "stop-auto-chasing";
    }
    return RECRUITMENT_REFERENCE_STATUSES.find(
      (status) => status.id === statusId,
    )?.color;
  };

  const followUpHistoryItems = followUpLogs.map((log) => ({
    id: log.id,
    label: getFollowUpLabel(log),
    tone: getFollowUpColor(log)?.replace("orange600", "orange-600"),
    createdAt: log.updatedAt || log.editedAt,
    userDisplayName: log.updatedByName || log.updatedBy || log.editedBy || "Unknown user",
    notes:
      log.details?.reason
        ? `Reason: ${String(log.details.reason).replace(/-/g, " ")}`
        : [
              "reference_email_delivered",
              "reference_email_redelivered",
              "reference_email_resent",
              "reference_email_sent",
            ].includes(log.action) && log.details?.actualRecipients?.length
          ? `Sent to: ${log.details.actualRecipients.join(", ")}`
          : "",
  }));

  return (
    <section ref={sectionRef} className="reference-edit-logs bg-white p-m card-corner-radius fit-column gap-m">
      <div className="row-space-between align-center">
        <div className="FP_Body_Semibold text-black">Logs</div>
        <button
          type="button"
          className="reference-log-icon-button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? "Collapse logs" : "Expand logs"}
        >
          <ArrowDownIcon className={open ? "reference-log-arrow reference-log-arrow--open" : "reference-log-arrow"} />
        </button>
      </div>
      {open && (
        <div className="fit-column gap-m">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
          {loading && <div className="FP_Content grey">Loading edit logs...</div>}
          {!loading && activeTab === "follow-up-history" && (
            <ProgressHistory
              items={followUpHistoryItems}
              loading={false}
              title=""
              ariaLabel="Reference follow-up history"
              emptyMessage="Follow-up activity will appear here after an email is sent or skipped."
              showDuration={false}
              tooltipPosition="right"
            />
          )}
          {!loading && activeTab === "edit-logs" && editLogs.length === 0 && (
            <div className="FP_Content grey">No edit logs found yet. Saved changes and email actions will appear here.</div>
          )}
          {!loading && activeTab === "edit-logs" && editLogs.map((log) => (
            <details key={log.id} className="reference-edit-log-item">
              <summary className="reference-edit-log-summary">
                <div className="fit-column gap-xs">
                  <span className="FP_Content_Semibold">{log.updatedByName || log.updatedBy || log.editedBy || "Unknown user"}</span>
                  <span className="FP_Help_Text grey">Reference saved | {log.changes.length} {log.changes.length === 1 ? "change" : "changes"}</span>
                </div>
                <span className="FP_Help_Text grey">{formatDateTime(log.updatedAt || log.editedAt)}</span>
                <ArrowDownIcon className="reference-edit-log-summary-arrow" aria-hidden />
              </summary>
              <div className="fit-column gap-s reference-edit-log-body">
                {(log.changes || []).map((change, index) => (
                  <div key={`${log.id}-${change.fieldPath}-${index}`} className="reference-edit-log-change grid3 gap-m">
                    <span className="FP_Help_Text_Semibold">{formatField(change.fieldPath)}</span>
                    <span className="FP_Help_Text"><span className="grey">From:</span> {formatValue(change.previousValue, change.fieldPath)}</span>
                    <span className="FP_Help_Text"><span className="grey">To:</span> {formatValue(change.newValue, change.fieldPath)}</span>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </section>
  );
};

export default ReferenceEditLogs;
